import React, { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import Menu from "./Menu";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "./firebase";

const FONT_FAMILY = "'Montserrat', 'Open Sans', Arial, sans-serif";
const REQUIRED_HEADERS = ["track name", "track preview url"];
const PROJECT_ID = "bobquiz-272a7";
const REGION = "us-central1";
const FUNCTION_NAME = "api";
const BASE_LOCAL = `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}/${FUNCTION_NAME}`;

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase();
}

function buildHeaderMap(fields) {
  const map = new Map();
  (fields || []).forEach((field) => {
    const normalized = normalizeHeader(field);
    if (normalized) map.set(normalized, field);
  });
  return map;
}

function summarizeResults(results) {
  return results.reduce(
    (acc, item) => {
      if (item.status === "downloaded") acc.downloaded += 1;
      else if (item.status === "failed") acc.failed += 1;
      else acc.skipped += 1;
      acc.total += 1;
      return acc;
    },
    { downloaded: 0, skipped: 0, failed: 0, total: 0 }
  );
}

function isLocalhost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

async function postMusicDownloader(tracks) {
  const body = JSON.stringify({ tracks });

  if (isLocalhost()) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort("timeout"), 900);
      const res = await fetch(`${BASE_LOCAL}/music-downloader`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (res.ok) return res.json();
    } catch {
      // fall through to prod
    }
  }

  const res = await fetch(`${window.location.origin}/api/music-downloader`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  });
  let payload = null;
  let text = "";
  try {
    payload = await res.json();
  } catch {
    text = await res.text().catch(() => "");
  }
  if (!res.ok) {
    const detail = payload?.error || text || res.statusText || "Failed to process tracks";
    throw new Error(`Music downloader failed (${res.status}): ${detail}`);
  }
  if (!payload) throw new Error("Music downloader returned no data");
  return payload;
}

export default function MusicDownloader() {
  const [bgUrl, setBgUrl] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    getDownloadURL(ref(storage, "media/Brainbackground.png"))
      .then(setBgUrl)
      .catch(() => {});
  }, []);

  const summary = useMemo(() => summarizeResults(results), [results]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setResults([]);
    setError("");
  };

  const handleProcess = async () => {
    if (!selectedFile || isProcessing) return;
    setIsProcessing(true);
    setError("");
    setResults([]);
    setProgress("Reading CSV...");

    try {
      const text = await selectedFile.text();
      const cleaned = text.replace(/^\uFEFF/, "");
      const parsed = Papa.parse(cleaned, { header: true, skipEmptyLines: true });
      if (parsed?.errors?.length) {
        throw new Error(parsed.errors[0]?.message || "CSV parse error");
      }

      const headerMap = buildHeaderMap(parsed.meta?.fields || []);
      const missingHeaders = REQUIRED_HEADERS.filter((h) => !headerMap.has(h));
      if (missingHeaders.length) {
        throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
      }

      const nameHeader = headerMap.get("track name");
      const urlHeader = headerMap.get("track preview url");

      const localResults = [];
      const tracks = [];

      (parsed.data || []).forEach((row, index) => {
        const trackName = String(row?.[nameHeader] || "").trim();
        const previewUrl = String(row?.[urlHeader] || "").trim();

        if (!previewUrl) {
          localResults.push({
            index,
            trackName,
            status: "skipped",
            reason: "Missing Track Preview URL",
          });
          return;
        }

        if (!trackName) {
          localResults.push({
            index,
            trackName,
            status: "skipped",
            reason: "Missing Track Name",
          });
          return;
        }

        tracks.push({ index, trackName, previewUrl });
      });

      if (!tracks.length) {
        setResults(localResults);
        setProgress("No valid rows to process.");
        setIsProcessing(false);
        return;
      }

      setProgress(`Sending ${tracks.length} tracks for download...`);
      const payload = await postMusicDownloader(tracks);

      const merged = [...localResults, ...(payload.results || [])].sort((a, b) => {
        const aIndex = Number.isFinite(a.index) ? a.index : Number.MAX_SAFE_INTEGER;
        const bIndex = Number.isFinite(b.index) ? b.index : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });

      setResults(merged);
      setProgress("Done.");
    } catch (err) {
      setError(err?.message || "Failed to process CSV");
      setProgress("");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        background: bgUrl
          ? `linear-gradient(rgba(24,24,40,0.78), rgba(44,16,50,0.82)), url(${bgUrl}) center center / cover no-repeat`
          : "#1f2338",
        fontFamily: FONT_FAMILY,
        color: "#f2f5ff",
      }}
    >
      <div
        style={{
          width: "100%",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>
        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>
          Music Downloader
        </div>
      </div>

      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      <div
        style={{
          maxWidth: 980,
          margin: "30px auto",
          padding: "24px 28px",
          background: "rgba(12, 10, 22, 0.7)",
          borderRadius: 18,
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ fontSize: "1.2rem", fontWeight: 800, marginBottom: 12 }}>
          Upload CSV of Track Preview URLs
        </div>
        <div style={{ opacity: 0.85, marginBottom: 16, lineHeight: 1.5 }}>
          Required columns: <strong>Track Name</strong> and{" "}
          <strong>Track Preview URL</strong> (case-insensitive).
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ color: "#e8ecff" }}
          />
          <button
            onClick={handleProcess}
            disabled={!selectedFile || isProcessing}
            style={{
              background: isProcessing ? "rgba(120,120,120,0.4)" : "rgba(81, 164, 255, 0.9)",
              border: "none",
              color: "#0c1020",
              fontWeight: 800,
              borderRadius: 10,
              padding: "10px 16px",
              cursor: selectedFile && !isProcessing ? "pointer" : "not-allowed",
            }}
          >
            {isProcessing ? "Processing..." : "Download Tracks"}
          </button>
          {selectedFile && (
            <span style={{ opacity: 0.85 }}>{selectedFile.name}</span>
          )}
        </div>

        {progress && <div style={{ marginBottom: 12 }}>{progress}</div>}
        {error && (
          <div
            style={{
              padding: "10px 12px",
              background: "rgba(255, 90, 90, 0.12)",
              border: "1px solid rgba(255, 90, 90, 0.4)",
              borderRadius: 10,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        {results.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                gap: 16,
                fontWeight: 700,
                marginBottom: 12,
              }}
            >
              <span>Downloaded: {summary.downloaded}</span>
              <span>Skipped: {summary.skipped}</span>
              <span>Failed: {summary.failed}</span>
              <span>Total: {summary.total}</span>
            </div>
            <div
              style={{
                maxHeight: 360,
                overflowY: "auto",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              {results.map((item, idx) => (
                <div
                  key={`${item.index ?? idx}-${item.trackName}-${item.status}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 0.4fr 1fr",
                    gap: 12,
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    fontSize: "0.95rem",
                  }}
                >
                  <div>{item.trackName || "(blank)"}</div>
                  <div
                    style={{
                      color:
                        item.status === "downloaded"
                          ? "#9effb0"
                          : item.status === "failed"
                          ? "#ffb5b5"
                          : "#ffe099",
                      fontWeight: 700,
                      textTransform: "capitalize",
                    }}
                  >
                    {item.status}
                  </div>
                  <div style={{ opacity: 0.85 }}>
                    {item.reason || item.fileName || ""}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
